import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";

// Validate that the script content matches expected Meta Pixel patterns
const isValidMetaPixelScript = (content: string): boolean => {
  // Check for expected Meta Pixel patterns
  const hasFbqInit = /fbq\s*\(\s*['"]init['"]/.test(content);
  const hasPixelId = /fbq\s*\(\s*['"]init['"]\s*,\s*['"][0-9]+['"]/.test(content);
  const hasConnectFacebook = /connect\.facebook\.net/.test(content);
  
  // Block dangerous patterns
  const hasDangerousPatterns = 
    /eval\s*\(/.test(content) ||
    /Function\s*\(/.test(content) ||
    /document\.cookie/.test(content) ||
    /localStorage\.getItem/.test(content) ||
    /sessionStorage\.getItem/.test(content) ||
    /XMLHttpRequest/.test(content) ||
    /fetch\s*\((?!.*facebook)/.test(content) ||
    /window\.location\s*=/.test(content);
  
  return (hasFbqInit || hasPixelId || hasConnectFacebook) && !hasDangerousPatterns;
};

// Validate noscript content - should only contain img tags pointing to Facebook
const isValidMetaPixelNoscript = (content: string): boolean => {
  // Allow only img tags pointing to facebook.com
  const sanitized = content.trim();
  const hasOnlyImgTag = /^<img[^>]+>$/.test(sanitized);
  const pointsToFacebook = /src=["']https:\/\/www\.facebook\.com\/tr/.test(sanitized);
  
  return hasOnlyImgTag && pointsToFacebook;
};

// Defer script execution to improve TTI and FID
const deferExecution = (callback: () => void) => {
  // Wait for page to be fully interactive before loading analytics
  const loadAfterInteractive = () => {
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(callback, { timeout: 5000 });
    } else {
      setTimeout(callback, 4000);
    }
  };
  
  // Use longer delay to avoid blocking first input
  if (document.readyState === 'complete') {
    setTimeout(loadAfterInteractive, 1500);
  } else {
    window.addEventListener('load', () => setTimeout(loadAfterInteractive, 1500), { once: true });
  }
};

export const MetaPixelSnippet = () => {
  const [snippet, setSnippet] = useState<string>("");
  const [isExecuted, setIsExecuted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "get-public-settings",
        );
        if (error) return;
        if (data?.meta_pixel_snippet) setSnippet(data.meta_pixel_snippet);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (!snippet || isExecuted) return;

    // Extract script content
    const scriptMatch = snippet.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
    const scriptContent = scriptMatch ? scriptMatch[1].trim() : '';

    // Extract noscript content
    const noscriptMatch = snippet.match(/<noscript[^>]*>([\s\S]*?)<\/noscript>/i);
    const noscriptContent = noscriptMatch ? noscriptMatch[1].trim() : '';

    // Validate script content before executing
    const isScriptValid = scriptContent ? isValidMetaPixelScript(scriptContent) : true;
    const isNoscriptValid = noscriptContent ? isValidMetaPixelNoscript(noscriptContent) : true;

    if (!isScriptValid) {
      console.warn("Meta Pixel script failed validation - script not loaded for security reasons");
      return;
    }

    if (!isNoscriptValid) {
      console.warn("Meta Pixel noscript failed validation - content not loaded for security reasons");
    }

    if (!scriptContent && !noscriptContent) return;

    // Defer execution to avoid blocking initial page interactivity
    deferExecution(() => {
      try {
        // Add script
        if (scriptContent && isScriptValid) {
          const scriptElement = document.createElement('script');
          scriptElement.type = 'text/javascript';
          scriptElement.textContent = scriptContent;
          document.head.appendChild(scriptElement);
        }

        // Add noscript content as fallback image (won't execute but maintains tracking fallback)
        if (isNoscriptValid && noscriptContent) {
          const sanitizedNoscript = DOMPurify.sanitize(noscriptContent, { 
            ALLOWED_TAGS: ['img'], 
            ALLOWED_ATTR: ['src', 'height', 'width', 'style'] 
          });
          const noscriptElement = document.createElement('noscript');
          noscriptElement.innerHTML = sanitizedNoscript;
          document.body.appendChild(noscriptElement);
        }

        setIsExecuted(true);
      } catch (error) {
        console.error("Error executing Meta Pixel script:", error);
      }
    });
  }, [snippet, isExecuted]);

  return null;
};
